<template>
  <div class="selection-tree">
    <div class="selection-tree__top">
      <selection-tree-node
        isTopLevel
        :key="child.key"
        v-for="child in currentNode.children"
        :node="child"
        @onchange="onchange"
      ></selection-tree-node>
    </div>
    <span class="selection-tree__dropdown" @click="toggleKeywordTree"
      >{{ $t('dialog.keywords.' + (keywordTreeOpened ? 'hide-all' : 'show-all')) }}
      <BaseIcon :name="chevronIcon" class="selection-tree__chevron" />
    </span>

    <transition name="dropdown-basic">
      <div class="selection-tree__trees" v-show="keywordTreeOpened">
        <div class="selection-tree__tree">
          <selection-tree-node
            :key="child.key"
            :class="{ 'is-selected': child === chosenNode }"
            v-for="child in categories"
            :node="child"
            @onchange="onchange"
            @onclick="onclick(child)"
          ></selection-tree-node>
        </div>
        <div class="selection-tree__tree large" v-if="chosenNode !== null">
          <selection-tree-node
            :key="child2.key"
            v-for="child2 in chosenNode.children"
            :node="child2"
            @onchange="onchange"
            @onclick="onclick"
          ></selection-tree-node>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import SelectionTreeNode from '@/components/selection-tree/SelectionTreeNode';
import { checked, indeterminate, recursive } from '@/components/selection-tree/recursive-utils';

export default {
  components: {
    BaseIcon,
    SelectionTreeNode,
  },
  props: {
    root: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      currentNode: this.root,
      parents: [],
      clickedNode: null,
      keywordTreeOpened: false,
    };
  },
  watch: {
    root(value) {
      const currentKey = this.currentNode.key;
      this.parents = [];
      this.currentNode = value;

      // Try to find an existing node with same key than last current node
      let currentFound = this.findRecursive(this.currentNode, currentKey);
      if (currentFound) {
        this.currentNode = currentFound;
      }
    },
  },
  computed: {
    chevronIcon() {
      return this.keywordTreeOpened ? 'chevron-up' : 'chevron-down';
    },
    chosenNode() {
      return this.clickedNode;
    },
    checked() {
      return checked(this.currentNode);
    },
    indeterminate() {
      return indeterminate(this.currentNode);
    },
    categories() {
      return this.root.children.reduce((children, language) => children.concat(language.children), []);
    },
  },
  methods: {
    toggleKeywordTree() {
      this.keywordTreeOpened = !this.keywordTreeOpened;
    },
    onclick(node) {
      if (this.clickedNode === node) {
        this.clickedNode = null;
        return;
      }
      this.clickedNode = node;
    },
    onchange({ value, node }) {
      recursive(node, (node) => {
        node.checked = value;
      });

      this.triggerValueChange();
    },
    triggerValueChange() {
      const newValues = this.getValues();
      this.$emit('onchange', newValues);
    },
    getValues() {
      const array = [];

      recursive(this.root, (node) => {
        if (node.checked) {
          array.push(node.value);
        }
      });
      return array;
    },
    findRecursive(item, findKey) {
      if (item.key === findKey) return item;

      if (item.children) {
        this.parents.push(item);
        for (let i = 0; i < item.children.length; i++) {
          let current = this.findRecursive(item.children[i], findKey);
          if (current) {
            return current;
          }
        }
        this.parents.pop();
      }
    },
    clearSelection() {
      this.clickedNode = null;
      this.keywordTreeOpened = false;
    },
  },
  mounted() {
    this.keywordTreeOpened = false;
  },
};
</script>

<style lang="scss">
.selection-tree {
  background-color: $color-white;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;

  &__top {
    padding: 16px 0;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  }

  &__dropdown {
    text-decoration: underline;
    font-size: 14px;
    cursor: pointer;
    align-items: center;
    display: flex;
  }

  &__chevron {
    margin-left: 8px;
    width: 16px;
  }

  &__trees {
    padding-top: 16px;
    display: flex;
    width: 100%;
    flex-direction: row;
  }

  &__tree {
    max-height: 450px;
    overflow: auto;
    overscroll-behavior: contain;
    margin-right: 3px;
    width: 30%;

    &.large {
      width: 67%;
    }
  }

  @media screen and (max-width: 980px) {
    &__tree {
      width: 80%;

      &.large {
        width: 80%;
      }
    }
  }
}
</style>
